import React, { useRef, useEffect, useCallback } from 'react';
import { Upload, Mic, Square } from 'lucide-react';
import { AudioEngine } from '../AudioEngine';
import { formatTime } from '../utils';

export function Waveform({
  audioUrl, duration, onTimeUpdate, isPlaying, setIsPlaying, engineRef, currentTime,
  onUpload, onRecord, onStopRecord, isRecording, recCountdown, isAnalyzing, analyzeStatus, filename, liveChunksRef
}: {
  audioUrl: string;
  duration: number;
  onTimeUpdate: (t: number) => void;
  isPlaying: boolean;
  setIsPlaying: (p: boolean) => void;
  engineRef: React.MutableRefObject<AudioEngine | null>;
  currentTime: number;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRecord: () => void;
  onStopRecord: () => void;
  isRecording: boolean;
  recCountdown: number;
  isAnalyzing: boolean;
  analyzeStatus?: string;
  filename: string;
  liveChunksRef: React.MutableRefObject<Float32Array[]>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const peaksRef = useRef<Float32Array | null>(null);

  // Load audio into engine
  useEffect(() => {
    const engine = new AudioEngine();
    engine.onTimeUpdate = onTimeUpdate;
    engine.onFinish = () => setIsPlaying(false);
    engineRef.current = engine;

    engine.load(audioUrl).then(() => {
      // Render waveform from decoded buffer
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const numBins = Math.floor(rect.width * 2);
        peaksRef.current = engine.getPeaks(numBins);
        drawWaveform(canvas);
      }
    });

    return () => { engine.destroy(); engineRef.current = null; };
  }, [audioUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const drawWaveform = useCallback((canvas?: HTMLCanvasElement | null) => {
    const c = canvas || canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const rect = c.getBoundingClientRect();
    const targetW = Math.floor(rect.width * 2);
    const targetH = Math.floor(rect.height * 2);
    if (c.width !== targetW || c.height !== targetH) {
      c.width = targetW;
      c.height = targetH;
    }
    ctx.setTransform(2, 0, 0, 2, 0, 0);
    const W = rect.width;
    const H = rect.height;
    const mid = H / 2;
    const labelW = 40;
    const plotW = W - labelW;
    const barW = 2;
    const barGap = 1;
    const step = barW + barGap;

    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, W, H);

    // Live recording mode: draw chunks as they arrive
    if (isRecording && liveChunksRef.current.length > 0) {
      const chunks = liveChunksRef.current;
      const totalSamples = chunks.reduce((s, c2) => s + c2.length, 0);
      const samplesPerBar = Math.max(1, Math.floor(totalSamples / Math.floor(plotW / step)));
      let sampleIdx = 0, chunkIdx = 0, posInChunk = 0;
      for (let i = labelW; i < W; i += step) {
        let maxVal = 0;
        for (let s = 0; s < samplesPerBar && chunkIdx < chunks.length; s++) {
          const v = Math.abs(chunks[chunkIdx][posInChunk]);
          if (v > maxVal) maxVal = v;
          posInChunk++;
          if (posInChunk >= chunks[chunkIdx].length) { chunkIdx++; posInChunk = 0; }
        }
        const h = maxVal * mid * 0.9;
        ctx.fillStyle = '#f87171';
        ctx.beginPath();
        ctx.roundRect(i, mid - h, barW, h * 2, 1);
        ctx.fill();
      }
      // Recording time display
      const recDur = totalSamples / 48000;
      ctx.fillStyle = '#f87171';
      ctx.font = '10px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(recDur.toFixed(1) + 's', W - 5, H - 4);
      return;
    }

    // Countdown animation: pulsing ring
    if (recCountdown > 0) {
      const cx = W / 2, cy = mid;
      const r = Math.min(W, H) * 0.15;
      const angle = (Date.now() / 400) % (Math.PI * 2); // spinning

      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(cx, cy, r, angle, angle + Math.PI * 1.4);
      ctx.stroke();
      ctx.lineCap = 'butt';
      return;
    }

    const peaks = peaksRef.current;
    if (!peaks) return;

    const engine = engineRef.current;
    const dur = engine?.duration || duration;
    const progress = engine ? engine.currentTime / dur : 0;
    const progressX = labelW + progress * plotW;

    // Draw bars in the plot area (after labelW offset)
    for (let i = labelW; i < W; i += step) {
      const frac = (i - labelW) / plotW;
      const peakIdx = Math.floor(frac * peaks.length);
      const peak = peaks[peakIdx] || 0;
      const h = peak * mid * 0.9;
      ctx.fillStyle = i < progressX ? '#60a5fa' : '#4a4a6a';
      ctx.beginPath();
      ctx.roundRect(i, mid - h, barW, h * 2, 1);
      ctx.fill();
    }

    // Cursor
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(progressX, 0);
    ctx.lineTo(progressX, H);
    ctx.stroke();
  }, [duration, engineRef, isRecording, recCountdown]);

  // Redraw on time update (including external seeks from piano roll)
  useEffect(() => { drawWaveform(); }, [drawWaveform, isPlaying, currentTime]);

  // Redraw periodically during playback OR recording
  useEffect(() => {
    if (!isPlaying && !isRecording && !recCountdown) return;
    const interval = setInterval(() => drawWaveform(), isRecording ? 30 : 50);
    return () => clearInterval(interval);
  }, [isPlaying, drawWaveform]);

  // Resize — debounced to prevent ResizeObserver loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let rafId: number | null = null;
    const ro = new ResizeObserver(() => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const engine = engineRef.current;
        if (engine?.buffer) {
          const rect = canvas.getBoundingClientRect();
          peaksRef.current = engine.getPeaks(Math.floor(rect.width * 2));
        }
        drawWaveform();
      });
    });
    ro.observe(canvas);
    return () => { ro.disconnect(); if (rafId) cancelAnimationFrame(rafId); };
  }, [drawWaveform, engineRef]);

  const togglePlay = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.playPause();
    setIsPlaying(engine.isPlaying);
  }, [setIsPlaying, engineRef]);

  // Click to seek on waveform (account for labelW offset matching PianoRoll)
  const handleClick = useCallback((e: React.MouseEvent) => {
    const engine = engineRef.current;
    if (!engine) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const labelW = 40;
    const x = e.clientX - rect.left;
    const plotW = rect.width - labelW;
    const t = Math.max(0, ((x - labelW) / plotW)) * engine.duration;
    engine.seek(t);
    onTimeUpdate(t);
    drawWaveform();
  }, [engineRef, drawWaveform, onTimeUpdate]);

  return (
    <div className="waveform-container">
      <div className="waveform-controls">
        <button className="play-btn" onClick={togglePlay}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <label className="control-btn" title="Upload audio">
          <Upload size={14} />
          <input type="file" accept=".mp3,.wav,.flac,.ogg,.m4a,.aac,.json" onChange={onUpload} hidden disabled={isAnalyzing || isRecording} />
        </label>
        <button
          className={`control-btn ${isRecording ? 'recording' : ''}`}
          onClick={isRecording ? onStopRecord : onRecord}
          disabled={isAnalyzing}
          title={isRecording ? 'Stop recording' : 'Record'}>
          {isRecording ? <Square size={14} /> : <Mic size={14} />}
        </button>
        <span className="waveform-filename">{isAnalyzing ? (analyzeStatus || 'Analyzing...') : filename}</span>
        <span className="waveform-spacer" />
        <span className="waveform-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
      </div>
      <canvas ref={canvasRef} className="waveform-canvas"
        onClick={handleClick} style={{ cursor: 'pointer' }} />
    </div>
  );
}
